import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { StockBalanceSummary } from '@aeris/contracts'
import {
  ZERO_QUANTITY,
  checkMovement,
  formatQuantity,
  parseQuantity,
} from '@aeris/inventory'
import { QUANTITY_SCALE, parseHumanAmount, toString } from '@aeris/money'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  MoneyInput,
  Textarea,
} from '@aeris/ui'

import {
  ApiError,
  adjustStock,
  recordStockEntry,
  recordStockExit,
} from '../lib/api.ts'

/**
 * Record a stock movement.
 *
 * One dialog for the three operations, because they differ only in a verb and a
 * permission — three near-identical components would drift apart within a month
 * and the difference would be a bug nobody looks for.
 *
 * What a person types is `1.234,5`; what the server receives is `1234.5`. The
 * translation is @aeris/money's `parseHumanAmount`, which is a separate,
 * deliberately lenient parser from the strict one that reads machine formats —
 * see ADR-0016. At no point does the value pass through a JavaScript number.
 *
 * The negative-stock rule is checked here too, using the same `checkMovement`
 * the server uses (`@aeris/inventory` is imported by both, so they cannot
 * drift). That is a courtesy, not the enforcement: the server refuses
 * independently under a row lock, which is the only place the answer can be
 * true.
 */

export type StockOperation = 'entry' | 'exit' | 'adjustment'

const TITLES: Record<StockOperation, string> = {
  entry: 'Registrar entrada',
  exit: 'Registrar saída',
  adjustment: 'Ajustar para a quantidade contada',
}

const DESCRIPTIONS: Record<StockOperation, string> = {
  entry: 'Uma entrada é registrada no histórico e não pode ser apagada.',
  exit: 'Uma saída é registrada no histórico e não pode ser apagada.',
  adjustment:
    'Informe o que existe fisicamente. O sistema calcula a diferença e registra o ajuste com o seu nome.',
}

const QUANTITY_LABELS: Record<StockOperation, string> = {
  entry: 'Quantidade que entrou',
  exit: 'Quantidade que saiu',
  adjustment: 'Quantidade contada',
}

export type StockMovementFormProps = {
  operation: StockOperation
  balance: StockBalanceSummary
  onClose: () => void
  /**
   * O movimento foi gravado (F248). A entrada pode tirar o produto de
   * "aguardando estoque" ou do arquivado até ter estoque, e isso muda a versão
   * dele: quem tem o Editar aberto precisa da versão nova para salvar.
   */
  onRecorded?: (() => void) | undefined
}

export const StockMovementForm = ({
  operation,
  balance,
  onClose,
  onRecorded,
}: StockMovementFormProps): ReactNode => {
  const queryClient = useQueryClient()

  const [amount, setAmount] = useState('')
  const [unitCost, setUnitCost] = useState('')
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const onHand = parseQuantity(balance.onHand) ?? ZERO_QUANTITY

  /**
   * The typed value, exactly.
   *
   * `null` while the field is empty or half-typed, which is a normal state and
   * not an error — so nothing is said about it until the form is submitted.
   */
  const typed =
    amount.trim().length === 0 ? null : parseHumanAmount(amount, QUANTITY_SCALE)

  /**
   * The same rule the server applies, from the same package.
   *
   * A courtesy, not the enforcement: the server checks again under a row lock,
   * which is the only place the answer can still be true by the time it is
   * acted on.
   */
  const wouldGoNegative =
    operation === 'exit' &&
    typed !== null &&
    !checkMovement(onHand, 'out', typed).ok

  const quantityError = ((): string | undefined => {
    if (amount.trim().length === 0) return undefined
    if (typed === null)
      return 'Informe um número. Use vírgula para os decimais.'
    if (typed.units < 0n) return 'A quantidade não pode ser negativa.'
    // Zero is a legitimate count — "the shelf is empty" — but never a movement.
    if (typed.units === 0n && operation !== 'adjustment') {
      return 'A quantidade deve ser maior que zero.'
    }
    if (wouldGoNegative) {
      return `Estoque insuficiente. Disponível: ${formatQuantity(onHand)}.`
    }
    return undefined
  })()

  const mutation = useMutation({
    mutationFn: async () => {
      if (typed === null) throw new Error('unreachable: guarded by onSubmit')

      // The exact value, in the machine format the contract requires.
      const quantity = toString(typed)
      const cleanNote = note.trim().length === 0 ? null : note.trim()

      switch (operation) {
        case 'entry': {
          const cost =
            unitCost.trim().length === 0
              ? null
              : parseHumanAmount(unitCost, QUANTITY_SCALE)
          return await recordStockEntry({
            variantId: balance.variantId,
            branchId: balance.branchId,
            quantity,
            unitCost: cost === null ? null : toString(cost),
            note: cleanNote,
          })
        }
        case 'exit':
          return await recordStockExit({
            variantId: balance.variantId,
            branchId: balance.branchId,
            quantity,
            note: cleanNote,
          })
        case 'adjustment':
          return await adjustStock({
            variantId: balance.variantId,
            branchId: balance.branchId,
            counted: quantity,
            note: cleanNote,
          })
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stock-balances'] })
      await queryClient.invalidateQueries({ queryKey: ['stock-movements'] })
      // A lista de Produtos mostra o saldo, e a ficha abre por cima dela (F131).
      await queryClient.invalidateQueries({ queryKey: ['products'] })
      onRecorded?.()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível registrar. Tente novamente.'
      )
    },
  })

  const onSubmit = (): void => {
    setFormError(null)
    if (typed === null) {
      setFormError('Informe a quantidade.')
      return
    }
    if (quantityError !== undefined) return
    mutation.mutate()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={TITLES[operation]}
        description={DESCRIPTIONS[operation]}
        className="max-w-lg"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit()
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {formError === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {formError}
            </p>
          )}

          <div className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-2">
            <p className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
              {balance.productName}
            </p>
            <p className="font-(--font-mono) text-(length:--text-xs) text-(--text-secondary)">
              {balance.sku}
            </p>
            <p className="mt-1 text-(length:--text-xs) text-(--text-secondary)">
              Saldo atual:{' '}
              <strong className="font-(--font-mono) text-(--text-primary)">
                {formatQuantity(onHand)} {balance.unit}
              </strong>
            </p>
          </div>

          <Field
            label={QUANTITY_LABELS[operation]}
            required
            error={quantityError}
            hint={`Use vírgula para os decimais. Unidade: ${balance.unit}`}
          >
            {(field) => (
              <Input
                {...field}
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value)
                }}
                autoFocus
                inputMode="decimal"
                placeholder="0"
                className="font-(--font-mono)"
              />
            )}
          </Field>

          {/*
            Only on an entry, and only because valuation needs it later.
            Average cost and FIFO (out of scope here) cannot be computed
            retroactively from movements that never carried a cost.
          */}
          {operation === 'entry' ? (
            <Field
              label="Custo unitário"
              hint="Opcional. Guardado para a valorização de estoque futura"
            >
              {(field) => (
                <MoneyInput
                  {...field}
                  value={unitCost}
                  onValueChange={setUnitCost}
                />
              )}
            </Field>
          ) : null}

          <Field
            label="Observação"
            hint={
              operation === 'adjustment'
                ? 'Recomendado: explique o motivo do ajuste'
                : 'Opcional'
            }
          >
            {(field) => (
              <Textarea
                {...field}
                value={note}
                onChange={(event) => {
                  setNote(event.target.value)
                }}
                rows={2}
              />
            )}
          </Field>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
              disabled={quantityError !== undefined}
            >
              Registrar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
